import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import NatureIcon from "@mui/icons-material/Nature";
import ParkIcon from "@mui/icons-material/Park";
import SpaIcon from "@mui/icons-material/Spa";
import EmojiNatureIcon from "@mui/icons-material/EmojiNature";
import GrassIcon from "@mui/icons-material/Grass";

const getIcon = (key) => {
  switch (key.toLowerCase()) {
    case "habit":
      return <ParkIcon sx={{ color: "#2E7D32" }} />;

    case "bark":
    case "branchlets":
      return <NatureIcon sx={{ color: "#2E7D32" }} />;

    case "leaves":
      return <SpaIcon sx={{ color: "#2E7D32" }} />;

    case "flowers":
      return <EmojiNatureIcon sx={{ color: "#2E7D32" }} />;

    case "fruit":
      return <GrassIcon sx={{ color: "#2E7D32" }} />;

    case "inflorescence":
      return <SearchIcon sx={{ color: "#2E7D32" }} />;

    default:
      return <NatureIcon sx={{ color: "#2E7D32" }} />;
  }
};

function DigitalIdentification({ plant }) {
  if (!plant?.digital_identification) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 5,
        p: {
          xs: 3,
          md: 4,
        },
        borderRadius: 5,
        background:
          "linear-gradient(135deg,#FFFFFF,#F8FBFC)",
        border: "1px solid #E4EDF2",
        boxShadow: "0 10px 30px rgba(11,60,93,.08)",
      }}
    >
      {/* Header */}

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        mb={3}
      >
        <Box
          sx={{
            width: 55,
            height: 55,
            borderRadius: "50%",
            bgcolor: "#EEF5FA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SearchIcon
            sx={{
              color: "#0B3C5D",
            }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: {
                xs: "1.5rem",
                md: "2rem",
              },
              fontWeight: 700,
              color: "#0B3C5D",
            }}
          >
            Digital Identification
          </Typography>

          <Typography
            color="text.secondary"
            fontSize={15}
          >
            Diagnostic botanical characters for accurate field
            identification
          </Typography>
        </Box>
      </Stack>

      {/* Accent */}

      <Box
        sx={{
          width: 90,
          height: 4,
          bgcolor: "#43A047",
          borderRadius: 10,
          mb: 4,
        }}
      />

      {/* Identification Cards */}

      <Grid container spacing={3}>
        {Object.entries(plant.digital_identification).map(
          ([key, value]) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={key}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 4,
                  background:
                    "linear-gradient(135deg,#FFFFFF,#F9FCFD)",
                  border: "1px solid #E5EDF3",
                  transition: ".35s",
                  boxShadow:
                    "0 6px 18px rgba(11,60,93,.05)",

                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow:
                      "0 15px 35px rgba(11,60,93,.12)",
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  mb={2}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      bgcolor: "#EEF8F2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {getIcon(key)}
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#0B3C5D",
                    }}
                  >
                    {key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) =>
                        c.toUpperCase()
                      )}
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    color: "#555",
                    lineHeight: 1.9,
                    textAlign: "justify",
                    fontSize: "0.98rem",
                  }}
                >
                  {value}
                </Typography>
              </Paper>
            </Grid>
          )
        )}
      </Grid>

      {/* Footer */}

      <Box
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 4,
          bgcolor: "#F5FAF7",
          borderLeft: "5px solid #43A047",
          display: "flex",
          gap: 2,
          alignItems: "flex-start",
        }}
      >
        <SearchIcon
          sx={{
            color: "#2E7D32",
            mt: 0.3,
          }}
        />

        <Typography
          sx={{
            color: "#555",
            lineHeight: 1.9,
          }}
        >
          <strong style={{ color: "#0B3C5D" }}>
            Identification Note:
          </strong>{" "}
          The diagnostic characters listed above are intended to
          assist researchers, students, botanists, and field
          survey teams in accurately identifying the species
          using observable morphological features. These
          characters are based on standard botanical
          descriptions and herbarium records.
        </Typography>
      </Box>
    </Paper>
  );
}

export default DigitalIdentification;